import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Users, FileText, Clock, CalendarCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const WEEK = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

const Kachel = ({ icon: Icon, titel, wert, zusatz, testid, onClick }) => (
  <button onClick={onClick} data-testid={testid} disabled={!onClick}
    className={`relative text-left border border-[#E3DECE] bg-[#FAF5F1] p-5 transition-all duration-200 ${
      onClick ? "hover:border-[#BA6D52] hover:-translate-y-0.5" : "cursor-default"}`}>
    <div className="flex items-start justify-between gap-3">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">{titel}</p>
      <Icon size={15} className="text-[#BA6D52] shrink-0" />
    </div>
    <p className="font-serif text-2xl text-[#2C2A26] mt-3 leading-tight">{wert}</p>
    <p className="text-xs text-[#757067] mt-1 line-clamp-2">{zusatz}</p>
  </button>
);

export const PortalKpis = ({ pwd, onOpenTab }) => {
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    const headers = { "X-Admin-Password": pwd };
    const get = async (pfad) => {
      try { return (await axios.get(`${API}/${pfad}`, { headers })).data || []; }
      catch { return []; }
    };
    const [kunden, seiten, quotes, nachweise, kalender] = await Promise.all([
      get("admin/kunden/uebersicht"), get("admin/angebotsseiten"), get("admin/quotes"),
      get("admin/stundennachweise"), get("availability"),
    ]);

    const heute = new Date().toISOString().slice(0, 10);
    const monat = heute.slice(0, 7);
    const offeneSeiten = seiten.filter((s) => s.status === "offen" && !s.abgelaufen);
    const offeneQuotes = quotes.filter((q) => !["accepted", "cancelled", "expired", "declined"].includes(q.status));
    const offenerWert = offeneSeiten.reduce((a, s) => a + Number(s.summen?.netto || 0), 0);

    const offeneStunden = nachweise.filter((s) => s.confirmed_at && !s.invoiced_at);
    const stunden = offeneStunden.reduce((a, s) => a + Number(s.hours || 0), 0);
    const stundenNetto = offeneStunden.reduce((a, s) => a + Number(s.total_net_eur || 0), 0);

    const termine = [
      ...kalender.filter((x) => x.status === "booked" && x.date >= heute).map((x) => ({ datum: x.date, was: "Gebuchter Tag" })),
      ...seiten.filter((s) => s.status === "angenommen" && s.datum >= heute)
        .map((s) => ({ datum: s.datum, was: s.firma || s.anlass || "Auftrag" })),
    ].sort((a, b) => a.datum.localeCompare(b.datum));
    const next = termine[0];

    setD({
      kunden: kunden.length,
      neuImMonat: kunden.filter((k) => (k.created_at || "").slice(0, 7) === monat).length,
      angebote: offeneSeiten.length + offeneQuotes.length,
      angebotWert: offenerWert,
      stunden, stundenNetto,
      next,
    });
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  const nextLabel = d.next
    ? (() => {
        const dt = new Date(`${d.next.datum}T12:00:00`);
        const tage = Math.round((dt - new Date(`${new Date().toISOString().slice(0, 10)}T12:00:00`)) / 86400000);
        if (tage === 0) return "Heute";
        if (tage === 1) return "Morgen";
        if (tage < 7) return WEEK[dt.getDay()];
        return d.next.datum.split("-").reverse().join(".");
      })()
    : "—";

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8 animate-in fade-in slide-in-from-bottom-2 duration-300"
      data-testid="portal-kpis">
      <Kachel icon={Users} titel="Kunden" wert={d.kunden} testid="kpi-kunden"
        zusatz={d.neuImMonat ? `${d.neuImMonat} neu diesen Monat` : "angelegte Stammkunden"} />
      <Kachel icon={FileText} titel="Offene Angebote" wert={d.angebote} testid="kpi-angebote"
        zusatz={d.angebotWert > 0 ? `${eur(d.angebotWert)} netto im Rennen` : "warten auf Antwort"}
        onClick={() => onOpenTab?.("angebote")} />
      <Kachel icon={Clock} titel="Offene Stunden" wert={`${d.stunden.toFixed(2).replace(".", ",")} Std.`}
        testid="kpi-stunden"
        zusatz={d.stundenNetto > 0 ? `${eur(d.stundenNetto)} abrechenbar` : "nichts offen"}
        onClick={() => onOpenTab?.("stundenkonto")} />
      <Kachel icon={CalendarCheck} titel="Nächster Einsatz" wert={nextLabel} testid="kpi-naechster"
        zusatz={d.next ? d.next.was : "kein Termin eingetragen"}
        onClick={() => onOpenTab?.("availability")} />
    </div>
  );
};

export default PortalKpis;
